/**
 * AI Prompt Modal
 *
 * Initial prompt UI for generating AI presentations.
 */

import React, { useState, useCallback } from 'react';
import { useAI } from './context/AIContext';
import { AIGenerationOptions } from './services/AIService';
import { StyleTheme } from '../../ai/schemas/StyleTheme';

interface AIPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (sessionId: string) => void;
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'creative', label: 'Creative' },
  { value: 'educational', label: 'Educational' },
] as const;

const SLIDE_COUNT_OPTIONS = [3, 5, 7, 10, 15, 20];

export function AIPromptModal({ isOpen, onClose, onSuccess }: AIPromptModalProps) {
  const {
    generate,
    isLoading,
    error,
    clearError,
    startGeneration,
    setStage,
    complete,
    fail,
    availableThemes,
    selectedTheme,
    setSelectedTheme,
  } = useAI();

  const [prompt, setPrompt] = useState('');
  const [slideCount, setSlideCount] = useState(5);
  const [tone, setTone] = useState<AIGenerationOptions['tone']>('professional');
  const [audience, setAudience] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!prompt.trim()) return;

      clearError();
      startGeneration();

      const success = await generate(prompt, {
        slideCount,
        tone,
        audience: audience || undefined,
        theme: selectedTheme.id,
      });

      if (success) {
        setStage('processing');
        setTimeout(() => {
          setStage('compiling');
          setTimeout(() => {
            complete();
            if (onSuccess) {
              // The session ID is now available through the context
            }
          }, 500);
        }, 500);
      } else {
        fail(error || 'Generation failed');
      }
    },
    [
      prompt,
      slideCount,
      tone,
      audience,
      selectedTheme,
      generate,
      clearError,
      startGeneration,
      setStage,
      complete,
      fail,
      onSuccess,
      error,
    ]
  );

  if (!isOpen) return null;

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <h2>Create with AI</h2>
          <button className="ai-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ai-modal-content">
          <div className="ai-form-group">
            <label htmlFor="ai-prompt">Describe your presentation</label>
            <textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., A 5-slide pitch deck for a sustainable fashion startup targeting Gen Z consumers..."
              rows={4}
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="ai-form-row">
            <div className="ai-form-group ai-form-half">
              <label htmlFor="ai-slides">Number of slides</label>
              <select
                id="ai-slides"
                value={slideCount}
                onChange={(e) => setSlideCount(Number(e.target.value))}
                disabled={isLoading}
              >
                {SLIDE_COUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} slides
                  </option>
                ))}
              </select>
            </div>

            <div className="ai-form-group ai-form-half">
              <label htmlFor="ai-tone">Tone</label>
              <select
                id="ai-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as AIGenerationOptions['tone'])}
                disabled={isLoading}
              >
                {TONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            className="ai-toggle-advanced"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>

          {showAdvanced && (
            <div className="ai-advanced-options">
              <div className="ai-form-group">
                <label htmlFor="ai-audience">Target audience (optional)</label>
                <input
                  id="ai-audience"
                  type="text"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="E.g., investors, students, marketing team..."
                  disabled={isLoading}
                />
              </div>

              <div className="ai-form-group">
                <label>Theme</label>
                <div className="ai-theme-grid">
                  {availableThemes.slice(0, 6).map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      className={`ai-theme-option ${selectedTheme.id === theme.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTheme(theme)}
                      disabled={isLoading}
                      style={{
                        backgroundColor: theme.colors.background,
                        color: theme.colors.textPrimary,
                        borderColor: theme.colors.primary,
                      }}
                    >
                      <span
                        className="ai-theme-accent"
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      {theme.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="ai-error">
              <span className="ai-error-icon">!</span>
              {error}
            </div>
          )}

          <div className="ai-modal-footer">
            <button
              type="button"
              className="ai-btn ai-btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ai-btn ai-btn-primary"
              disabled={isLoading || !prompt.trim()}
            >
              {isLoading ? 'Generating...' : 'Generate Presentation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AIPromptModal;
